import { StreamType } from '../../types'
import { Message, PMChatState, PMState } from '../../types/mfc'
import { goToMessage } from '../actions/nav'
import { pubSub } from '../app-state/pubSub'
import { PMPlaySoundOnUeopt, SettingsType } from '../constants/enums'
import { avatarUrl } from '../mfcLogic/avatarPreviewUrl'
import { MfcNotification } from '../mfcLogic/notification'
import { playDingSound } from '../mfcLogic/utilities'
import { isQuickReplyOpen } from './modals/state.floater'
import { stateWrapper, stateWrapperShortform } from './state'
import { SETTINGSSTATE } from './state.settings'
import { USERSSTATE } from './state.users'

pubSub.USERS.on(
    'UPDATE:streamType',
    ({
        previous: previousStreamType,
        current: streamType,
        id,
    }: {
        previous: StreamType
        current: StreamType
        id: number
    }) => {
        if (streamType === 'club') {
            removePMChatState(id, 'clubShow')
        }

        const isPublicShow = !streamType || streamType === 'public'
        const action = isPublicShow ? 'exited' : 'entered'
        const showType = isPublicShow ? previousStreamType : streamType

        if (!showType) {
            return
        }

        const message = {
            text: `You have ${action} the ${showType} show.`,
            success: !isPublicShow,
            warning: isPublicShow,
            timestamp: Date.now(),
            showStatus: true,
            system: true,
        }

        setPMChatUniqueState(id, message, 'showStatus')
    },
)

pubSub.USERS.on(
    'UPDATE:isClubShow',
    ({
        previous: wasClubShow,
        current: isClubShow,
        id,
    }: {
        previous: boolean
        current: boolean
        id: number
    }) => {
        if (wasClubShow && !isClubShow && PMSTATE.chats[id]) {
            removePMChatState(id, 'clubShow')
        }
    },
)

const getStartOfDayTimestamp = (timestamp) => {
    return new Date(new Date(timestamp).toDateString()).getTime()
}

export const PMSTATE: PMState = {
    startOfTodayTimestamp: getStartOfDayTimestamp(Date.now()),
    isBatchLoading: true,
    unreadFragments: {},
    isSearching: false,
    isExhausted: false,
    skipPubsub: false,
    isLoading: false,
    activeID: null,
    unread: 0,
    chats: {},
}

const PMCHATSTATE: PMChatState = {
    isIntentionallyClosed: false,
    wasPMCommandUsed: false,
    listHasToday: false,
    isExhausted: false,
    isLoading: false,
    isOpen: false,
    messages: [],
    history: [],
    latest: [],
    unread: 0,
    activeThisSession: false,
}

export const emitPMNotification = (id, text) => {
    if (!SETTINGSSTATE[SettingsType.DEFAULT].pmNotification) {
        return
    }

    const { slug, name = slug } = USERSSTATE[id] || {}

    if (!slug) {
        console.warn(
            '[Message APP State]: ' +
                'Cannot notify user because of missing slug. ' +
                'This is not supposed to happen!',
        )
        return
    }

    void MfcNotification.new({
        message: 'Private Message from ' + name,
        icon: avatarUrl(id, 150),
        body: text,
        onclick: () => {
            void goToMessage(slug)
        },
    })
}

const emitState = (id?) => {
    if (id) {
        pubSub.MESSAGES.emit(`state::${id}`, PMSTATE.chats[id])
        return
    }

    pubSub.MESSAGES.emit(`state`, { ...PMSTATE })
}

const sW = stateWrapper({ state: PMSTATE, update: emitState })
const sWSF = stateWrapperShortform({ state: PMSTATE, update: emitState })

const chatStateWrapper =
    (execute, key?, options = { diffInput: true, diffOutput: false }) =>
    (id, ...args) => {
        createChatRecord(id)

        const { diffInput = true, diffOutput } = options

        if (key && diffInput) {
            const [value] = args

            if (PMSTATE.chats[id][key] === value) {
                // don't update if it's the same value
                return
            }
        }

        const previousValue = PMSTATE.chats[id][key]

        execute(id, ...args)

        if (key && diffOutput && PMSTATE.chats[id][key] === previousValue) {
            // don't update if it's the same value
            return
        }

        PMSTATE.chats[id] = { ...PMSTATE.chats[id] }
        PMSTATE.chats = { ...PMSTATE.chats }

        if (PMSTATE.skipPubsub) {
            return
        }

        emitState(id)
    }

const chatStateWrapperShortform = (key, options?) =>
    chatStateWrapper(
        (id: number, value: any) => {
            PMSTATE.chats[id][key] = value
        },
        key,
        options,
    )

export const createChatRecord = (id) => {
    if (PMSTATE.chats[id]) {
        return
    }

    PMSTATE.chats[id] = JSON.parse(JSON.stringify(PMCHATSTATE))

    return PMSTATE.chats[id]
}

export const setPMUnreadState = sW((unreadDiff: number) => {
    const { unread: previousUnread = 0 } = PMSTATE
    PMSTATE.unread = previousUnread + unreadDiff
})

export const setPMUnreadStateFragment = (id: number, unread: number) => {
    PMSTATE.unreadFragments[id] = unread
    setPMUnreadState(unread)
}

export const setPMBatchLoadingState = sWSF('isBatchLoading')
export const setPMSearchingState = sWSF('isSearching')
export const setPMExaustedState = sWSF('isExhausted')
export const setPMActiveIDState = sWSF('activeID')
export const setPMLoadingState = sWSF('isLoading')

export const setPMChatWasPMCommandUsedState =
    chatStateWrapperShortform('wasPMCommandUsed')
export const setPMChatExaustedState = chatStateWrapperShortform('isExhausted')
export const setPMChatLoadingState = chatStateWrapperShortform('isLoading')

export const setPMChatOpenState = chatStateWrapper(
    (id: number, isOpen: boolean) => {
        PMSTATE.chats[id].isOpen = isOpen

        if (isOpen) {
            setPMActiveIDState(id)
            setPMChatIntentionallyClosedState(id, false)
            return
        }

        setPMActiveIDState(null)
    },
    'isOpen',
)

export const setPMChatUnreadState = chatStateWrapper(
    (id: number, unread: number) => {
        const {
            chats: { [id]: chat },
        } = PMSTATE

        const { unread: previousUnread = 0, isOpen } = chat

        if ((isOpen || isQuickReplyOpen(id, true)) && unread > 0) {
            return
        }

        if (PMSTATE.unreadFragments[id]) {
            setPMUnreadState(-PMSTATE.unreadFragments[id])
            delete PMSTATE.unreadFragments[id]
        }

        PMSTATE.chats[id].unread = unread

        setPMUnreadState(unread - previousUnread)
    },
    'unread',
)

export const setPMChatIntentionallyClosedState = chatStateWrapper(
    (id: number, isIntentionallyClosed: boolean) => {
        if (isIntentionallyClosed && PMSTATE.activeID === id) {
            setPMActiveIDState(null)
        }

        PMSTATE.chats[id].isIntentionallyClosed = isIntentionallyClosed
    },
    'isIntentionallyClosed',
)

const createDateMarker = (timestamp) => {
    const startOfDayTimestamp = getStartOfDayTimestamp(timestamp)
    const date = new Date(startOfDayTimestamp)
    const datetime = date.toDateString() + ' ' + date.toTimeString()

    return {
        timestamp: startOfDayTimestamp,
        id: `marker-${datetime}`,
        text: datetime,
        marker: true,
    }
}

const setPMChatHistory = chatStateWrapper((id, message) => {
    const {
        chats: { [id]: existingChat },
        startOfTodayTimestamp,
    } = PMSTATE

    const { listHasToday, history, latest } = existingChat
    const { timestamp } = message
    const incomingStartOfDayTimestamp = getStartOfDayTimestamp(timestamp)

    if (!history[0] && incomingStartOfDayTimestamp === startOfTodayTimestamp) {
        if (listHasToday) {
            history[0] = latest[0]
            existingChat.latest = latest.slice(1)
        }

        existingChat.listHasToday = true
    }

    const marker = history[0]

    if (!marker || marker?.timestamp !== incomingStartOfDayTimestamp) {
        history.unshift(createDateMarker(timestamp))
    }

    existingChat.history = [history[0], message, ...history.slice(1)]
    existingChat.messages = [...existingChat.history, ...latest]
})

const setPMChatLatest = chatStateWrapper((id, message) => {
    const { system } = message
    const {
        chats: { [id]: existingChat },
        startOfTodayTimestamp,
    } = PMSTATE
    const { listHasToday, history, latest } = existingChat

    if (!listHasToday && !system) {
        existingChat.listHasToday = true
        latest.push({
            datetime: new Date(startOfTodayTimestamp).toDateString(),
            timestamp: startOfTodayTimestamp,
            marker: true,
            today: true,
        })
    }

    existingChat.latest = [...latest, message]
    existingChat.messages = [...history, ...existingChat.latest]
})

const uniqueID = {}

export const setPMChatTextState = (id: number, message: Message) => {
    const { warning, history, system, text, raw, news, info, from, me } =
        message
    const {
        chats: { [id]: existingChat = {} as PMChatState },
    } = PMSTATE
    const { messages: existingMessages } = existingChat

    if (!uniqueID[id]) {
        uniqueID[id] = 1
    }

    const messageWithID = { ...message, id: ++uniqueID[id] }

    if (history) {
        setPMChatHistory(id, messageWithID)
        return
    }

    if (system) {
        const systemMessage: Message = {
            ...messageWithID,
            timestamp: Date.now(),
            pm: true,
            warning,
            system,
            from,
            text,
            info,
        }

        PMSTATE.chats[id].messages = [...existingMessages, systemMessage]
        setPMChatLatest(id, systemMessage)
        return
    }

    setPMChatLatest(id, messageWithID)

    const { isOpen } = PMSTATE.chats[id]

    if (!isOpen) {
        emitPMNotification(id, raw)
    }

    if (!news) {
        const activeThisSession = PMSTATE.chats[id].activeThisSession
        const playSoundOn = SETTINGSSTATE[SettingsType.DEFAULT].pmPlaySoundsOn
        if (playSoundOn === PMPlaySoundOnUeopt.none) return
        const shouldPlaySound =
            (!me && playSoundOn === PMPlaySoundOnUeopt.receivedMessage) ||
            playSoundOn === PMPlaySoundOnUeopt.allMessages ||
            (playSoundOn === PMPlaySoundOnUeopt.onlyOnFirstMessage &&
                !activeThisSession &&
                !me)
        const notificationSound =
            SETTINGSSTATE[SettingsType.DEFAULT].pmNotificationSound
        if (shouldPlaySound) playDingSound(notificationSound, !me)
        if (!me) PMSTATE.chats[id].activeThisSession = true
    }
}

const filterOutChatMessage = (id: number, filter: (() => string) | string) => {
    if (typeof filter === 'function') {
        PMSTATE.chats[id].latest = PMSTATE.chats[id].latest.filter(filter)
    }

    if (typeof filter !== 'string') {
        console.warn(
            '[filterOutChatMessage] Filter must be a function or string.',
        )
        return
    }

    PMSTATE.chats[id].latest = PMSTATE.chats[id].latest.filter((data) => {
        const { [filter]: prop } = data
        return !prop
    })
}

export const setPMChatUniqueState = (
    id: number,
    message: Message,
    filter: (() => string) | string,
) => {
    filterOutChatMessage(id, filter)
    setPMChatTextState(id, message)
}

export const setPMChatOnlyIfMissingState = (
    id: number,
    message: Message,
    find: (() => string) | string,
) => {
    if (typeof find === 'string') {
        const found = PMSTATE.chats[id].messages.find((data) => {
            const { [find]: prop } = data
            return !prop
        })

        if (found) {
            return
        }
    }

    if (typeof find === 'function') {
        const found = PMSTATE.chats[id].messages.find(find)

        if (found) {
            return
        }
    }

    setPMChatTextState(id, message)
}

export const removePMChatState = chatStateWrapper(
    (id: number, filter: (() => string) | string) => {
        filterOutChatMessage(id, filter)

        const {
            chats: { [id]: existingChat = {} as PMChatState },
        } = PMSTATE

        const { history, latest } = existingChat

        existingChat.messages = [...history, ...latest]
    },
)

export const setPMChatNewsState = (message: Message) => {
    const { activeID } = PMSTATE

    if (!activeID) {
        return
    }

    setPMChatTextState(activeID, message)
}

if (typeof window !== 'undefined') {
    window.__MFC_APP_MESSAGES__ = {
        PMSTATE,
        createChatRecord,
        emitState,
        setPMChatIntentionallyClosedState,
        setPMChatWasPMCommandUsedState,
        setPMChatOnlyIfMissingState,
        setPMChatExaustedState,
        setPMBatchLoadingState,
        setPMChatLoadingState,
        setPMChatUnreadState,
        setPMChatUniqueState,
        setPMSearchingState,
        setPMChatTextState,
        setPMExaustedState,
        setPMChatOpenState,
        setPMChatNewsState,
        setPMActiveIDState,
        setPMLoadingState,
        removePMChatState,
        setPMUnreadState,
    }
}
